import React from 'react';
import Colors from '@/constants/colors';

// Lexical JSON types
interface LexicalTextNode {
  type: 'text';
  text: string;
  format: number; // Bitmask: 1=bold, 2=italic, 4=strikethrough, 8=underline, 16=code, 32=subscript, 64=superscript
  style?: string;
}

interface LexicalLineBreakNode {
  type: 'linebreak';
}

interface LexicalLinkNode {
  type: 'link';
  url: string;
  children: LexicalNode[];
}

interface LexicalElementNode {
  type: 'root' | 'paragraph' | 'heading' | 'list' | 'listitem' | 'quote';
  children: LexicalNode[];
  tag?: string; // For headings: h1, h2, etc.
  listType?: 'bullet' | 'number';
  direction?: 'ltr' | 'rtl' | null;
  format?: string;
  indent?: number;
}

type LexicalNode = LexicalTextNode | LexicalLineBreakNode | LexicalLinkNode | LexicalElementNode;

interface LexicalRoot {
  root: LexicalElementNode;
}

// Text format bitmask values
const FORMAT_BOLD = 1;
const FORMAT_ITALIC = 2;
const FORMAT_STRIKETHROUGH = 4;
const FORMAT_UNDERLINE = 8;
const FORMAT_CODE = 16;

// Styles extracted outside component for performance
const styles: Record<string, React.CSSProperties> = {
  paragraph: {
    fontSize: '16px',
    lineHeight: '24px',
    color: Colors.text,
    margin: '0 0 16px 0',
  },
  h1: {
    fontSize: '28px',
    fontWeight: '700',
    lineHeight: '1.2',
    color: Colors.text,
    margin: '24px 0 16px 0',
  },
  h2: {
    fontSize: '24px',
    fontWeight: '700',
    lineHeight: '1.2',
    color: Colors.text,
    margin: '20px 0 12px 0',
  },
  h3: {
    fontSize: '20px',
    fontWeight: '600',
    lineHeight: '1.3',
    color: Colors.text,
    margin: '16px 0 8px 0',
  },
  h4: {
    fontSize: '18px',
    fontWeight: '600',
    lineHeight: '1.3',
    color: Colors.text,
    margin: '12px 0 8px 0',
  },
  h5: {
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '1.4',
    color: Colors.text,
    margin: '8px 0 4px 0',
  },
  h6: {
    fontSize: '14px',
    fontWeight: '600',
    lineHeight: '1.4',
    color: Colors.textSecondary,
    margin: '8px 0 4px 0',
  },
  list: {
    margin: '0 0 16px 0',
    paddingLeft: '24px',
  },
  listItem: {
    fontSize: '16px',
    lineHeight: '24px',
    color: Colors.text,
    marginBottom: '4px',
  },
  quote: {
    borderLeft: `3px solid ${Colors.primary}`,
    paddingLeft: '16px',
    margin: '16px 0',
    fontStyle: 'italic',
    color: Colors.textSecondary,
  },
  link: {
    color: Colors.primary,
    textDecoration: 'underline',
  },
  code: {
    fontFamily: 'monospace',
    backgroundColor: Colors.ice,
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '14px',
  },
};

function renderTextNode(node: LexicalTextNode, key: string): React.ReactNode {
  let content: React.ReactNode = node.text;

  if (node.format & FORMAT_CODE) {
    content = <code key={`${key}-code`} style={styles.code}>{content}</code>;
  }
  if (node.format & FORMAT_BOLD) {
    content = <strong key={`${key}-bold`}>{content}</strong>;
  }
  if (node.format & FORMAT_ITALIC) {
    content = <em key={`${key}-italic`}>{content}</em>;
  }
  if (node.format & FORMAT_UNDERLINE) {
    content = <u key={`${key}-underline`}>{content}</u>;
  }
  if (node.format & FORMAT_STRIKETHROUGH) {
    content = <s key={`${key}-strike`}>{content}</s>;
  }

  return content;
}

function renderNode(node: LexicalNode, key: string): React.ReactNode {
  if (node.type === 'text') {
    return renderTextNode(node, key);
  }

  if (node.type === 'linebreak') {
    return <br key={key} />;
  }

  if (node.type === 'link') {
    return (
      <a
        key={key}
        href={node.url}
        style={styles.link}
        target="_blank"
        rel="noopener noreferrer"
      >
        {node.children.map((child, i) => renderNode(child, `${key}-${i}`))}
      </a>
    );
  }

  // Element nodes with children
  const children = node.children?.map((child, i) => renderNode(child, `${key}-${i}`));

  switch (node.type) {
    case 'root':
      return <div key={key}>{children}</div>;

    case 'paragraph':
      // Skip empty paragraphs
      if (!children || children.length === 0) {
        return <br key={key} />;
      }
      return <p key={key} style={styles.paragraph}>{children}</p>;

    case 'heading': {
      const tag = node.tag || 'h1';
      const headingStyle = styles[tag] || styles.h1;
      switch (tag) {
        case 'h1': return <h1 key={key} style={headingStyle}>{children}</h1>;
        case 'h2': return <h2 key={key} style={headingStyle}>{children}</h2>;
        case 'h3': return <h3 key={key} style={headingStyle}>{children}</h3>;
        case 'h4': return <h4 key={key} style={headingStyle}>{children}</h4>;
        case 'h5': return <h5 key={key} style={headingStyle}>{children}</h5>;
        case 'h6': return <h6 key={key} style={headingStyle}>{children}</h6>;
        default: return <h1 key={key} style={headingStyle}>{children}</h1>;
      }
    }

    case 'list': {
      const ListTag = node.listType === 'number' ? 'ol' : 'ul';
      return <ListTag key={key} style={styles.list}>{children}</ListTag>;
    }

    case 'listitem':
      return <li key={key} style={styles.listItem}>{children}</li>;

    case 'quote':
      return <blockquote key={key} style={styles.quote}>{children}</blockquote>;

    default:
      // Fallback for unknown node types - render children
      return children ? <>{children}</> : null;
  }
}

export function parseLexicalJSON(content: string): React.ReactNode {
  try {
    const parsed: LexicalRoot = JSON.parse(content);

    if (!parsed?.root?.children) {
      return <p style={styles.paragraph}>{content}</p>;
    }

    return renderNode(parsed.root, 'root');
  } catch {
    // If parsing fails, return as plain text
    return <p style={styles.paragraph}>{content}</p>;
  }
}

export function isLexicalJSON(content: string): boolean {
  if (!content) return false;
  try {
    const parsed = JSON.parse(content);
    return parsed?.root?.type === 'root' && Array.isArray(parsed?.root?.children);
  } catch {
    return false;
  }
}

/**
 * Extract plain text from Lexical JSON for previews/descriptions
 */
export function extractPlainText(content: string): string {
  if (!content) return '';

  if (!isLexicalJSON(content)) {
    return content;
  }

  try {
    const parsed: LexicalRoot = JSON.parse(content);
    return extractTextFromNode(parsed.root);
  } catch {
    return content;
  }
}

function extractTextFromNode(node: LexicalNode): string {
  if (node.type === 'text') {
    return node.text;
  }

  if (node.type === 'linebreak') {
    return ' ';
  }

  if ('children' in node && Array.isArray(node.children)) {
    return node.children.map(extractTextFromNode).join('');
  }

  return '';
}
